<script setup lang="ts">
import {QBtn, useDialogPluginComponent} from 'quasar'
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {ref, shallowRef, ShallowRef, watch, watchEffect} from "vue";
import Translator from "@/utils/common/Translator.ts";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {ColDef, GetRowIdParams} from "ag-grid-community";
import {AgGridVue} from "ag-grid-vue3";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import {
  CellKeyDownEvent,
  FullWidthCellKeyDownEvent,
  GetCellRendererInstancesParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowSelectionOptions
} from "ag-grid-enterprise";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {ListDialogProps} from "@/utils/presenters/interfaces.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import NaMaConfirmationDialog from "@/utils/forms/NaMaConfirmationDialog.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import QuestionListHandler from "@/utils/common/QuestionListHandler.ts";
import AGGridEditViewColumnFactory from "@/utils/UIConstants/AGGridEditViewColumns/AGGridEditViewColumnFactory.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import MetaData from "@/utils/common/MetaData.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import FieldMetaData = namaTypes.FieldMetaData;
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;

const props = defineProps<ListDialogProps>();

const okButton = ref<QBtn | undefined>();
const {compactMode} = storeToRefs(useNamaFlagsStore());
const {dialogRef, onDialogCancel, onDialogOK} = useDialogPluginComponent();
const loginStore = useLoginInfoStore();
const rowSelection: RowSelectionOptions = {
  mode: "multiRow",
  checkboxes: true,
  headerCheckbox: true
};
let questionField = props.questionField;
const messagesStore = useMessagesStore();
const fieldId: string = questionField?.id ?? '';
const gridId: string = fieldId + '.' + QuestionFieldsPresenter.detailId;
const gridApi: ShallowRef<GridApi | undefined> = shallowRef();
let fieldMetaData: FieldMetaData = questionField?.metaData ? NamaCommon.cloneObject(questionField?.metaData) : {};
const fullColumnId = gridId + "." + fieldId;
const rowData = ref<any[]>();

fieldMetaData.fieldId = fullColumnId;
fieldMetaData.id = fullColumnId;
fieldMetaData.referencedEntityType = props.referencedEntityType;
fieldMetaData.allowedValues = questionField.allowedValues?.map(a => a.value).filter(a => !!a) || []
let column: EditScreenGridColumn = NamaCommon.cloneObject(questionField);
column.id = fullColumnId;

const handler = new QuestionListHandler(gridId, props.presenter, fieldMetaData, column);

watchEffect(async () => {
  let rows = NamaCommon.getFieldValue(props.presenter?.fetchRecord(), gridId) ?? [];
  if (rows && rows.length < 1) {
    rows.push({
      id: await ClientCache.asyncNextGUIDId()
    });
  }
  rowData.value = rows;
});


function fetchColDefs(): ColDef[] {
  let columnMetadata: FieldMetaData = MetaData.findFieldMetadata(column.id!, handler.metadata);
  let colDefs: ColDef[] = [];
  if (columnMetadata.fieldType === FieldType.Reference) {
    let codeColumn: EditScreenGridColumn = {
      id: column.id + "." + CommonFieldIds.CODE,
      title: {
        resourceId: CommonFieldIds.CODE
      }
    };
    let codeColDef = AGGridEditViewColumnFactory.createColDefForType(codeColumn, handler);
    if (codeColDef !== null) {
      let colDefsResult = codeColDef.createColDef();
      colDefs.push(...colDefsResult);
      handler.presenter.addDisabledQuestionFieldId(codeColumn.id ?? '');
    }
  }
  let colDef = AGGridEditViewColumnFactory.createColDefForType(column, handler);
  if (colDef !== null) {
    let colDefsResult = colDef.createColDef();
    colDefs.push(...colDefsResult);
  }
  return colDefs;
}

async function onCellKeyDown(event: CellKeyDownEvent | FullWidthCellKeyDownEvent) {
  let keyboardEvent = event.event! as KeyboardEvent;
  let api = event.api;
  let currentRowData = event.data as Record<string, any>;
  let rowId = currentRowData.id;
  let rowIndex = event.rowIndex!;
  let rows: any[] = NamaCommon.getFieldValue(props.presenter?.fetchRecord(), gridId);
  let realRowIndex = ObjectChecker.findIndexOf(rows, l => l.id === rowId)!;///
  let focusedCell = api.getFocusedCell();
  if (keyboardEvent.key === 'Insert') {
    if (keyboardEvent.ctrlKey) {
      if (keyboardEvent.altKey) {
        if (AGGridUtils.isEmptyLine(currentRowData)) {
          messagesStore.pushMessageStr("Could not duplicate empty line");
          return;
        }
        await duplicateMultipleRowsDialog(currentRowData, api, realRowIndex);
      } else {
        if (AGGridUtils.isEmptyLine(currentRowData)) {
          messagesStore.pushMessageStr("Could not insert new line while you have an empty line");
          return;
        }
        if (!api.isAnyFilterPresent() || await NaMaConfirmationDialog.okCancelConfirmationDialog(
          Translator.translateId('areYouSureToAddLinesThatMayNotBeDisplayed'), true))
          await AGGridUtils.asyncCreateRows(currentRowData, api, realRowIndex, false, props.presenter, gridId);
      }
    } else if (keyboardEvent.shiftKey) {
      if (AGGridUtils.isEmptyLine(currentRowData)) {
        messagesStore.pushMessageStr("Could not duplicate empty line");
        return;
      }
      await AGGridUtils.asyncCreateRows(currentRowData, api, realRowIndex, true, props.presenter, gridId);
    }
  } else if (keyboardEvent.key === 'Delete' && keyboardEvent.ctrlKey) {
    api.applyTransaction({remove: [currentRowData]});
    rows.splice(realRowIndex, 1);
    rows = NamaCommon.getFieldValue(props.presenter?.fetchRecord(), gridId);
    api.applyTransaction({update: rows});
    if (rows.length === 0) {
      let newLine = {id: ClientCache.syncNextGUIDId()};
      api.applyTransaction({add: [newLine], addIndex: 0});
      rows.push(newLine);
      let columns = api.getColumns()!;
      api.setFocusedCell(0, columns[0]);
    }
    if (focusedCell && rowIndex === api.getDisplayedRowCount())
      api.setFocusedCell(rowIndex - 1, focusedCell.column);
  } else if (keyboardEvent.key === 'ArrowDown' && focusedCell && AGGridUtils.isFocusedCellEditing(api, focusedCell)) {
    if (focusedCell.rowIndex + 1 < api.getDisplayedRowCount()) {
      api.setFocusedCell(rowIndex + 1, focusedCell.column);
      return;
    }
    if (api.isAnyFilterPresent() && !await NaMaConfirmationDialog.okCancelConfirmationDialog(Translator.translateId('areYouSureToAddLinesThatMayNotBeDisplayed'),
      true))
      return;
    AGGridUtils.createRow(currentRowData, api, realRowIndex + 1, props.presenter, gridId);
    if (rowIndex + 1 < api.getDisplayedRowCount())
      api.setFocusedCell(rowIndex + 1, api.getColumns()![0]);
  } else if (keyboardEvent.key === 'ArrowUp' && focusedCell && focusedCell.rowIndex > 0 && AGGridUtils.isFocusedCellEditing(api, focusedCell)) {
    api.setFocusedCell(focusedCell.rowIndex - 1, focusedCell.column);
  }
}


async function duplicateMultipleRowsDialog(currentRowData: Record<string, any>, api: GridApi, rowIndex: number) {
  if (AGGridUtils.isEmptyLine(currentRowData))
    //TODO:: Abdo -> maybe show some error, stating line is empty
    return;
  let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
    let count = record['count'];
    await AGGridUtils.asyncCreateRows(currentRowData, api, rowIndex, true, props.presenter, gridId, count)
  });
  let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
  });
  let questionField: QuestionField = NamaCommon.createQuestionField("count", FieldType.Integer);
  let defaultValue: DTOFilledQuestionField = {id: 'count', value: '1', objValue: 1};
  let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("count"), true);
  await dialogBox.createPresenter(props.presenter, [questionField], [defaultValue]);
  await dialogBox.runDialogBox();
}

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
  if (fieldMetaData.fieldType === FieldType.Reference) {
    setTimeout(() => {
      const firstRowNode: IRowNode | undefined = gridApi.value?.getDisplayedRowAtIndex(0);
      const params: GetCellRendererInstancesParams = {columns: [fieldId], rowNodes: firstRowNode ? [firstRowNode] : undefined};
      const instances = gridApi.value?.getCellRendererInstances(params);
      if (instances && instances.length > 0) {
        const $ = (instances[0] as any).$;
        if (instances[0] && $ && $.exposed && $.exposed.onBrowseClicked) {
          $.exposed.onBrowseClicked(props.clickEvent);
        }
      }
    }, 200);
  }
}

async function onInsertSelectedClick() {
  let selectedRows = AGGridUtils.fetchSelectedRows(gridApi.value!);
  if (selectedRows && selectedRows.length) {
    await QuestionFieldsPresenter.setQuestionListValue(props.presenter.fetchRecord(), gridId, selectedRows)
    onDialogOK();
  }
}

function onDeleteAllClick() {
  NamaCommon.setFieldValue(props.presenter.fetchRecord(), gridId, []);
}

function onDeleteSelectedClick() {
  let selectedRows = AGGridUtils.fetchSelectedRows(gridApi.value!);
  if (selectedRows && selectedRows.length) {
    let details: any[] = props.presenter.fetchRecord()[fieldId][QuestionFieldsPresenter.detailId] ?? [];
    let filteredList: any[] = details.filter(l => {
      if (!selectedRows.some(row => row.id === l.id))
        return l;
    });
    NamaCommon.setFieldValue(props.presenter.fetchRecord(), gridId, filteredList);
  }
}

async function onExtraButtonClick(action: Function | undefined) {
  if (action) {
    await action();
  }
}

watch(() => okButton.value,
  (value) => {
    if (value)
      okButton.value?.$el.focus()
  }
)
</script>

<template>

  <q-dialog persistent ref="dialogRef" @hide="onDialogCancel" v-if="loginStore.isLoggedIn" @keydown.esc="onDialogCancel">
    <q-card class="tw-flex tw-flex-col tw-w-full tw-h-[80%] tw-shadow-none tw-bg-namaBackground-bright-light dark:tw-text-namaFontColor-dark dark:tw-bg-namaBackground-bright-dark">
      <q-bar
        class="tw-flex-none tw-h-fit tw-min-h-8 tw-text-namaFontColor-light tw-bg-[#CED4DA] tw-px-2 tw-py-[6px] tw-flex tw-justify-between dark:tw-bg-[#212529] dark:tw-text-namaFontColor-dark">
        <div v-text="Translator.translateTitledId(props.presenter?.entityType, props.questionField)"/>
        <q-btn dense flat @click="onDialogCancel" :title="Translator.translateId( 'close')" class="tw-h-fit">
          <NamaIcon default-icon="nm-win-close"/>
        </q-btn>
      </q-bar>
      <q-card-section class="tw-w-full tw-h-full tw-flex tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-normal">
        <AgGridVue
          class="tw-grow"
          style="width: 100%; font-size: var(--ag-font-size);"
          rowModelType="clientSide"
          :columnDefs="fetchColDefs()"
          :rowSelection="rowSelection"
          :cellSelection="true"
          :rowNumbers="true"
          :suppressHeaderFocus="true"
          :enableRtl="Translator.isLanguageRTL()"
          :localeText="Translator.fetchAGGridLocale()"
          :rowData="rowData"
          :autoSizeStrategy='{type: "fitCellContents"}'
          :getRowId="(params: GetRowIdParams) => params.data.id"
          :stopEditingWhenCellsLoseFocus="false"
          :navigateToNextCell="AGGridUtils.onNavigationToNextCell(presenter, gridId)"
          :tabToNextCell="AGGridUtils.OnTabToNextCell(presenter, gridId)"
          @cellKeyDown="onCellKeyDown"
          @grid-ready="onGridReady"
        />
      </q-card-section>
      <q-card-section class="tw-flex tw-flex-row tw-pb-4 mobile:tw-pb-1 tw-gap-4 tw-overflow-x-auto tw-overflow-y-hidden desktop:tw-justify-center">
        <q-btn
          ref="okButton"
          :label="Translator.translateId('ok')"
          @click="onDialogOK"
          @keydown.enter="onDialogOK"
          :dense="compactMode"
          class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        <q-btn
          :label="Translator.translateId('insertSelected')"
          @click="onInsertSelectedClick"
          :dense="compactMode"
          class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        <q-btn
          :label="Translator.translateId('deleteAll')"
          @click="onDeleteAllClick"
          :dense="compactMode"
          class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        <q-btn
          ref="okButton"
          :label="Translator.translateId('deleteSelected')"
          @click="onDeleteSelectedClick"
          :dense="compactMode"
          class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        <template v-for="Button of extraButtons">
          <q-btn
            :label="Translator.translateId(Button.id)"
            @click="onExtraButtonClick(Button.action)"
            :dense="compactMode"
            class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark tw-text-base tw-font-normal tw-rounded-md tw-min-w-fit tw-w-fit"/>
        </template>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
